// Multi-line "textarea" backed by a `<div contenteditable>` so that
// `{TOKEN}` placeholders can render as atomic pill-spans. The component
// is fully controlled — `props.value` is a plain string with literal
// tokens, `props.onChange` fires with the latest serialised value.
//
// Design overview
// ===============
//  - Storage is a plain string. Pills exist only at render time.
//  - On mount / external `props.value` change → `parseValueToDOM` rebuilds
//    the DOM, but only when the serialised current DOM differs from
//    incoming props (avoids caret loss on every keystroke).
//  - On input → tokenise at caret (if mode permits) → serialise → emit.
//  - IME composition events defer tokenisation to `compositionend` so
//    partial CJK / accent input is intact.
//  - Add-field popover → `insertToken` drops a pill at the saved caret.
//  - Backspace / Delete next to a pill → browser handles natively;
//    `contenteditable="false"` makes the pill atomic to the browser's
//    own delete logic, and that goes through the native undo stack
//    so Ctrl+Z restores deleted pills. We never call `execCommand`.
//  - Copy / cut → plain-text via `serializeDOMToValue` so pills become
//    their literal `{TOKEN}` strings on the clipboard.
//  - Paste → plain-text in, re-tokenised through `parseValueToDOM`.
//  - View modes: `preview` (pills) vs `source` (literal text, fully
//    editable). Both round-trip to the same stored value.
//  - `singleLine` prop switches to an `<input>`-like layout: Enter never
//    inserts a break (and pasted newlines collapse to spaces), the sticky
//    toolbar is replaced by the same Add-field/View buttons rendered
//    icon-only in an overlay that fades in on hover at the end of the field.

import React, {createRef} from 'react'

import TextareaWithVariablesToolbar from "./textarea-with-variables-toolbar"
import {TextareaVariable, ViewMode} from './types'
import {textareaWithVariables_buildPill} from './pill/build'
import {textareaWithVariables_buildTokensMap} from './pill/build-tokens-map'
import {textareaWithVariables_normalizePillSpacers} from './pill/normalize-spacers'
import {textareaWithVariables_isSpacer} from './pill/is-spacer'
import {textareaWithVariables_parseValueToDOM} from './dom/parse-value'
import {textareaWithVariables_serializeDOMToValue} from './dom/serialize'
import {textareaWithVariables_isLogicallyEmpty} from './dom/is-empty'
import {textareaWithVariables_tokenizeAtCaret} from './editing/tokenize-at-caret'
import {textareaWithVariables_copySelectionToClipboard} from './editing/copy-to-clipboard'
import {textareaWithVariables_pastePlainText} from './editing/paste-text'
import {textareaWithVariables_handleEditorKeyDown} from './editing/handle-keydown'
import {textareaWithVariables_normalizeKeepingCaret} from './editing/normalize-keeping-caret'
import {textareaWithVariables_scrollCaretIntoView} from './editing/scroll-caret-into-view'
import {textareaWithVariables_handlePillMouseDown} from './editing/click-pill'
import {AddFieldConfig, textareaWithVariables_buildAddFieldConfig} from './add-field/build-config'


type Props = {
    value         : string
    onChange      : (text: string) => void
    tokens        : TextareaVariable[]

    placeholder?  : string
    disabled?     : boolean
    className?    : string
    rows?         : number
    singleLine?   : boolean   // one-line input: no newlines, no sticky toolbar — Add-field/View buttons overlay on hover instead
}


type State = {
    isEmpty : boolean
    mode    : ViewMode
}


// Empty tokens map (used as the `parseValueToDOM` input when the editor
// is in `source` mode) — kept as a module constant so we don't allocate on
// every render.
const EMPTY_TOKENS_MAP: Map<string, TextareaVariable> = new Map()


// If the collapsed caret sits at an element position immediately next to
// one of our zero-width spacer text nodes, return the equivalent position
// INSIDE that spacer (so the caret renders at the small font height).
// null = nothing to snap. Used by the selection-change snapper so a single
// mouse click near an edge pill lands on the small caret (no double-click).
function spacerSnapTarget(node: Node, offset: number): {node: Node, offset: number} | null {
    if (node.nodeType !== Node.ELEMENT_NODE) return null
    const after  = node.childNodes[offset] || null
    const before = offset > 0 ? node.childNodes[offset - 1] : null
    if (after && textareaWithVariables_isSpacer(after))   return {node: after, offset: 0}
    if (before && textareaWithVariables_isSpacer(before)) return {node: before, offset: (before.textContent || '').length}
    return null
}


export default class TextareaWithVariables extends React.Component<Props, State> {

    private editorRef       : React.RefObject<HTMLDivElement> = createRef()
    private tokensMap       : Map<string, TextareaVariable>   = new Map()
    private addFieldConfig  : AddFieldConfig

    // True while the user is mid-composition (IME). `input` events
    // during composition carry partial text and would corrupt the
    // composition if tokenised — so we defer to `compositionend`.
    private isComposing     : boolean = false

    // Some browsers fire a trailing `input` event right after
    // `compositionend` with the same composed string. We already
    // tokenise + emit in `compositionend`; this flag suppresses the
    // duplicate work on the very next `input`.
    private skipNextInput   : boolean = false

    // The most recent caret position observed *inside* the editor. The
    // Add-field popover steals focus on click, so by the time
    // `insertToken` runs the live selection is no longer in the
    // editor — we use this snapshot to restore the caret.
    private savedRange      : Range | null = null

    // Set while our selection snapper is programmatically moving the caret,
    // so the re-fired `selectionchange` doesn't recurse.
    private isSnapping      : boolean = false


    constructor(props: Props) {
        super(props)
        this.tokensMap      = textareaWithVariables_buildTokensMap(props.tokens)
        this.addFieldConfig = textareaWithVariables_buildAddFieldConfig(props.tokens)
        this.state = {
            isEmpty : !(props.value && props.value.length > 0),
            mode    : 'preview',
        }
    }


    // ── lifecycle ──────────────────────────────────────────────────

    componentDidMount() {
        this.renderValueIntoDOM(this.props.value || '')
        this.refreshEmptyState()
        document.addEventListener('selectionchange', this.onSelectionChange)
    }

    componentWillUnmount() {
        document.removeEventListener('selectionchange', this.onSelectionChange)
    }

    componentDidUpdate(prev: Props) {
        // Tokens list changed → rebuild map and Add-field config.
        if (prev.tokens !== this.props.tokens) {
            this.tokensMap      = textareaWithVariables_buildTokensMap(this.props.tokens)
            this.addFieldConfig = textareaWithVariables_buildAddFieldConfig(this.props.tokens)
        }
        // External value change → re-render the DOM, but only if the
        // current DOM doesn't already serialise to it (otherwise we'd
        // nuke the user's caret on every keystroke).
        const el = this.editorRef.current
        if (!el) return
        if (this.props.value !== prev.value && this.props.value !== textareaWithVariables_serializeDOMToValue(el)) {
            this.renderValueIntoDOM(this.props.value || '')
            this.refreshEmptyState()
        }
    }


    // ── selection tracking ────────────────────────────────────────

    // Snapshot the caret on every selection change so that subsequent
    // out-of-editor actions (Add-field popover, mode toggle) can
    // restore where the user was.
    private onSelectionChange = () => {
        const editor = this.editorRef.current
        if (!editor) return

        // Our own snap (below) moves the caret, which re-fires this event.
        if (this.isSnapping) { this.isSnapping = false; return }

        const sel = window.getSelection()
        if (!sel || sel.rangeCount === 0) return
        const range = sel.getRangeAt(0)
        if (!(editor.contains(range.startContainer) && editor.contains(range.endContainer))) return

        // Snap a collapsed caret that landed at an element position next to
        // a zero-width spacer INTO that spacer, so a single click lands on
        // the small in-text caret and the snapshot below is canonical.
        if (sel.isCollapsed) {
            const snap = spacerSnapTarget(range.startContainer, range.startOffset)
            if (snap) {
                this.isSnapping = true
                const r = document.createRange()
                r.setStart(snap.node, snap.offset)
                r.collapse(true)
                sel.removeAllRanges()
                sel.addRange(r)
                this.savedRange = r.cloneRange()
                this.keepCaretInViewIfSingleLine(editor)
                return
            }
        }

        this.savedRange = range.cloneRange()
        this.keepCaretInViewIfSingleLine(editor)
    }

    // Single-line editors scroll horizontally with `overflow: hidden` (no
    // visible scrollbar), which means the browser won't auto-scroll the
    // caret into view on its own — so every caret move nudges the scroll
    // position back onto the caret ourselves.
    private keepCaretInViewIfSingleLine(editor: HTMLDivElement) {
        if (this.props.singleLine) {
            textareaWithVariables_scrollCaretIntoView(editor)
        }
    }


    // ── DOM <-> state helpers ─────────────────────────────────────

    // Which tokens map should `parseValueToDOM` use right now? Source
    // view uses the empty map so `{TOKEN}` literals fall through to
    // plain text and are user-editable.
    private activeTokensMap(): Map<string, TextareaVariable> {
        return this.state.mode === 'source' ? EMPTY_TOKENS_MAP : this.tokensMap
    }

    // Replace the entire editor DOM with the freshly-parsed value.
    // Called on mount, on external value change, and on mode switch.
    private renderValueIntoDOM(value: string) {
        const el = this.editorRef.current
        if (!el) return
        el.innerHTML = ''
        el.appendChild(textareaWithVariables_parseValueToDOM(value, this.activeTokensMap()))
    }

    // Re-derive `isEmpty` from the live DOM and update state if
    // changed. Used to drive the placeholder visibility (`is-empty`
    // class). React state is the source of truth so the placeholder
    // doesn't flicker between renders.
    private refreshEmptyState = () => {
        const el = this.editorRef.current
        if (!el) return
        const empty = textareaWithVariables_isLogicallyEmpty(el)
        if (empty !== this.state.isEmpty) this.setState({isEmpty: empty})
    }

    // Serialise the DOM and call `props.onChange`. Also refreshes the
    // empty state — most input mutations potentially change it.
    private emitChange = () => {
        const el = this.editorRef.current
        if (!el) return
        this.props.onChange(textareaWithVariables_serializeDOMToValue(el))
        this.refreshEmptyState()
    }


    // ── input / composition handling ──────────────────────────────

    private onCompositionStart = () => { this.isComposing = true }

    private onCompositionEnd = () => {
        this.isComposing = false
        // composition produced its final text — tokenise (in preview
        // mode) and emit, then suppress the trailing `input` event
        // that browsers fire for the same composed string.
        this.skipNextInput = true
        if (this.state.mode === 'preview') {
            textareaWithVariables_tokenizeAtCaret(this.editorRef.current!, this.tokensMap, /*lastChar*/ undefined)
        }
        this.emitChange()
    }

    private onInput = (e: React.FormEvent<HTMLDivElement>) => {
        if (this.isComposing) return
        if (this.skipNextInput) {
            this.skipNextInput = false
            return
        }
        const native = e.nativeEvent as InputEvent
        // Only tokenise when the user actually inserted text (skip
        // e.g. delete-content events). `data` holds the inserted string.
        const inserted = native?.data ?? undefined

        const editor = this.editorRef.current
        if (editor && this.state.mode === 'preview') {
            textareaWithVariables_tokenizeAtCaret(editor, this.tokensMap, inserted)
            // A native delete (Backspace/Delete) that removed a pill can
            // leave a stray edge spacer behind — clean it up while keeping
            // the caret. (`inputType` is `deleteContent…` for those.)
            if (native && typeof native.inputType === 'string' && native.inputType.indexOf('delete') === 0) {
                textareaWithVariables_normalizeKeepingCaret(editor)
            }
        }
        this.emitChange()
    }

    // Clicking a pill (chip) should land the caret right next to it — see
    // click-pill.ts. Intercepted on mousedown, before the browser places
    // its own (inconsistent) caret for the atomic contenteditable=false
    // chip.
    private onEditorMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        const editor = this.editorRef.current
        if (editor) {
            textareaWithVariables_handlePillMouseDown(e.nativeEvent, editor)
        }
    }

    // Make the zero-width edge spacers transparent to horizontal arrows and
    // to Backspace/Delete (see handle-keydown). Source mode has no pills.
    private onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        // Single-line variant behaves like `<input>` — Enter never inserts a line break.
        if (this.props.singleLine && e.key === 'Enter') {
            e.preventDefault()
            return
        }
        if (this.state.mode !== 'preview') {
            return
        }
        const editor = this.editorRef.current
        if (editor) {
            textareaWithVariables_handleEditorKeyDown(e.nativeEvent, editor)
        }
    }


    // ── clipboard handlers ────────────────────────────────────────

    private onCopy = (e: React.ClipboardEvent<HTMLDivElement>) => {
        if (textareaWithVariables_copySelectionToClipboard(e)) e.preventDefault()
    }

    private onCut = (e: React.ClipboardEvent<HTMLDivElement>) => {
        if (textareaWithVariables_copySelectionToClipboard(e)) {
            e.preventDefault()
            const sel = window.getSelection()
            if (sel && sel.rangeCount > 0) {
                sel.getRangeAt(0).deleteContents()
                this.emitChange()
            }
        }
    }

    private onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault()
        const editor = this.editorRef.current
        if (!editor) {
            return
        }
        let text = e.clipboardData.getData('text/plain')
        if (this.props.singleLine) {
            text = text.replace(/[\r\n]+/g, ' ')
        }
        if (textareaWithVariables_pastePlainText(editor, text, this.activeTokensMap())) {
            this.emitChange()
        }
    }


    // ── Add-field / mode toggle ───────────────────────────────────

    // Drop a token at the saved caret. In source mode we insert the
    // literal `{TOKEN}` text node so the user can keep editing it;
    // in preview mode we insert a pill.
    private insertToken = (rawToken: string) => {
        const editor = this.editorRef.current
        if (!editor || this.props.disabled) return
        const def = this.tokensMap.get(rawToken)
        if (!def) return

        const isSource = this.state.mode === 'source'

        // Snapshot `savedRange` BEFORE calling `focus()` — focus can
        // fire a `selectionchange` event that overwrites our snapshot
        // with the editor's default caret (usually offset 0).
        const snapshot = this.savedRange
        editor.focus()

        // Prefer the snapshot; fall back to the live selection if it
        // happens to still be in the editor; final fallback is the
        // end of the editor.
        let range: Range
        if (snapshot && editor.contains(snapshot.startContainer)) {
            range = snapshot.cloneRange()
            range.deleteContents()
        } else {
            const sel = window.getSelection()
            if (sel && sel.rangeCount > 0 && editor.contains(sel.anchorNode)) {
                range = sel.getRangeAt(0)
                range.deleteContents()
            } else {
                range = document.createRange()
                range.selectNodeContents(editor)
                range.collapse(false)
            }
        }

        const inserted: Node = isSource
            ? document.createTextNode(def.token)
            : textareaWithVariables_buildPill(def)
        range.insertNode(inserted)

        // Normalise spacers FIRST — that may insert text nodes near
        // the new pill which would invalidate any pre-set range. Only
        // then place the caret right after the inserted node.
        if (!isSource) textareaWithVariables_normalizePillSpacers(editor)

        // Land the caret after the inserted node — and INSIDE its trailing
        // edge-spacer if normalise added one (gives the small caret at a
        // line end, and avoids an immediate snap).
        const tail = inserted.nextSibling
        const caret = document.createRange()
        if (!isSource && tail && textareaWithVariables_isSpacer(tail)) {
            caret.setStart(tail, (tail.textContent || '').length)
        } else {
            caret.setStartAfter(inserted)
        }
        caret.collapse(true)
        const sel = window.getSelection()
        sel?.removeAllRanges()
        sel?.addRange(caret)
        // Keep `savedRange` in sync so a follow-up Add-field click
        // lands right after the just-inserted node rather than a
        // stale position.
        this.savedRange = caret.cloneRange()

        this.emitChange()
    }

    // Switch the view between `preview` (pills) and `source` (literal
    // text). The DOM is the source of truth right at the click — we
    // serialise it, swap state, and re-render under the new mode.
    private setMode = (mode: ViewMode) => {
        if (mode === this.state.mode) return
        const el = this.editorRef.current
        const currentValue = el ? textareaWithVariables_serializeDOMToValue(el) : (this.props.value || '')
        this.setState({mode}, () => {
            this.renderValueIntoDOM(currentValue)
            this.refreshEmptyState()
            // The freshly-rendered DOM has no live caret yet; the old
            // snapshot is invalid against the new node identities.
            this.savedRange = null
        })
    }


    // ── render ────────────────────────────────────────────────────

    render() {
        const singleLine = this.props.singleLine
        const minHeight  = singleLine ? undefined : (this.props.rows || 3) * 26
        const isEmpty    = this.state.isEmpty

        const classes = Classes('ui5-editor-template', this.props.className, {
            'ui5-editor-template--single-line': singleLine
        })

        return (
            <div className={classes}>
                <div className="ui5-editor-template__frame">

                    {!this.props.disabled && !singleLine &&
                        <TextareaWithVariablesToolbar
                            config          = {this.addFieldConfig}
                            mode            = {this.state.mode}
                            onInsertToken   = {this.insertToken}
                            onSetMode       = {this.setMode}
                        />
                    }

                    <div ref                = {this.editorRef}
                         className          = {Classes('ui5-editor-template__editor ui5-txt-default', {'p-20': !singleLine, 'is-empty': isEmpty})}
                         style              = {singleLine ? undefined : {minHeight}}
                         contentEditable    = {!this.props.disabled}
                         suppressContentEditableWarning
                         data-placeholder   = {this.props.placeholder || ''}
                         spellCheck         = {false}
                         onMouseDown        = {this.onEditorMouseDown}
                         onKeyDown          = {this.onKeyDown}
                         onInput            = {this.onInput}
                         onPaste            = {this.onPaste}
                         onCopy             = {this.onCopy}
                         onCut              = {this.onCut}
                         onCompositionStart = {this.onCompositionStart}
                         onCompositionEnd   = {this.onCompositionEnd}/>

                    {!this.props.disabled && singleLine &&
                        <TextareaWithVariablesToolbar
                            config          = {this.addFieldConfig}
                            mode            = {this.state.mode}
                            onInsertToken   = {this.insertToken}
                            onSetMode       = {this.setMode}
                            iconOnly        = {true}
                        />
                    }
                </div>
            </div>
        )
    }
}

// Re-export so callers can import `TextareaVariable` from this file
// rather than reaching into internals.
export {TextareaVariable}
